import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { StatusBadge, TypeBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FolderKanban, Plus, Search, MoreHorizontal, MapPin, Pencil, Trash2, Map, CheckCircle2, Loader2, Navigation, UserPlus, X, Check, Phone, Mail, FileStack, ArrowUpDown, ArrowUp, ArrowDown, Building2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import WellMapVerification from '@/components/common/WellMapVerification';
import { useCounties } from '@/hooks/useCounties';
import 'leaflet/dist/leaflet.css';
import AttachCustomForm from '@/components/forms/AttachCustomForm';
import ProjectPipelineBadge, { useProjectsPipelinesBatch } from '@/components/projects/ProjectPipelineBadge';
import PaymentRibbon, { computePaymentRibbon } from '@/components/projects/PaymentRibbon';
import ContactNameStack from '@/components/common/ContactNameStack';
import { contactPrimaryLabel } from '@/lib/contactDisplay';
import { US_STATES, stateNameToCode } from '@/constants/usStates';

const TYPES = ['new_construction', 'repair', 'modification', 'abandonment'];

// Punto I (Alian): filtro rapido por estado del pipeline en la lista de
// Proyectos — "cliqué en pendiente y veo todo lo abierto y aceptado".
// Mapea las etapas granulares del pipeline (ver ProjectPipelineBadge.jsx)
// a 3 baldes simples + "All".
const STATUS_FILTER_STAGES = {
  pending: ['no_estimate', 'estimate_draft'],
  open: ['estimate_sent', 'invoice_sent', 'partial_payment', 'second_payment', 'waiting_for_permit', 'permit_approved', 'job_on_schedule'],
  accepted: ['estimate_approved', 'job_completed', 'invoice_paid'],
};

export default function ProjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const counties = useCounties();
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoicesByProject, setInvoicesByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // statusFilter intentionally removed from UI (replaced by per-column
  // sorting) — pipelineFilter (punto I) is a separate, simpler quick filter.
  const statusFilter = 'all';
  const [pipelineFilter, setPipelineFilter] = useState('all'); // all | pending | open | accepted
  const [sortBy, setSortBy] = useState('project_number');
  const [sortDir, setSortDir] = useState('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [autoProjectNumber, setAutoProjectNumber] = useState('');
  const [loadingProjectNumber, setLoadingProjectNumber] = useState(false);
  const [formsDialog, setFormsDialog] = useState({ open: false, project: null });
  const { pipelines } = useProjectsPipelinesBatch([projects.length]);

  // New client inline creation
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ first_name: '', last_name: '', company_name: '', phone: '', email: '', is_customer: true, type: 'customer', account_type: 'residential' });
  const [savingClient, setSavingClient] = useState(false);

  const defaultForm = {
    title: '', description: '', customer_id: '', type: 'new_construction', notes: '', office_number: '',
    location: { address: '', city: '', county: '', state: 'FL', zip_code: '', latitude: null, longitude: null, parcel_id: '' }
  };
  const [form, setForm] = useState(defaultForm);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [projRes, contRes, invRes] = await Promise.all([
        api.get('/projects', { params }),
        api.get('/contacts', { params: { page_size: 500 } }),
        api.get('/invoices', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setProjects(projRes.data);
      setContacts(contRes.data.items || contRes.data);
      const invItems = invRes.data.items || invRes.data || [];
      const byProject = {};
      for (const inv of invItems) {
        if (!inv.project_id) continue;
        (byProject[inv.project_id] = byProject[inv.project_id] || []).push(inv);
      }
      setInvoicesByProject(byProject);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [statusFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchNextProjectNumber = async () => {
    setLoadingProjectNumber(true);
    try {
      const res = await api.get('/projects/next-number');
      setAutoProjectNumber(res.data.project_number);
    } catch { setAutoProjectNumber(''); }
    finally { setLoadingProjectNumber(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setShowMap(false);
    fetchNextProjectNumber();
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setAutoProjectNumber(p.project_number || '');
    setForm({
      title: p.title || '', description: p.description || '', customer_id: p.customer_id || '',
      type: p.type || 'new_construction', notes: p.notes || '', office_number: p.office_number || '',
      location: {
        address: p.location?.address || '', city: p.location?.city || '',
        county: p.location?.county || '', state: stateNameToCode(p.location?.state) || 'FL',
        zip_code: p.location?.zip_code || p.location?.zip || '',
        latitude: p.location?.latitude || p.coordinates?.lat || null,
        longitude: p.location?.longitude || p.coordinates?.lng || null,
        parcel_id: p.location?.parcel_id || p.parcel_number || ''
      }
    });
    setShowMap(false);
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      const payload = {
        title: form.title,
        description: form.description,
        customer_id: form.customer_id,
        type: form.type,
        notes: form.notes,
        office_number: form.office_number,
        location: form.location
      };
      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success(t('success.updated'));
      } else {
        await api.post('/projects', payload);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success(t('success.deleted'));
      fetchData();
    } catch { toast.error(t('errors.deletingError')); }
  };

  const getCustomer = (id) => contacts.find(ct => ct.id === id) || null;
  const getCustomerName = (id) => contactPrimaryLabel(getCustomer(id));

  const handleAddressChange = (result) => {
    const newLocation = {
      ...form.location,
      address: result.address || form.location.address
    };
    if (result.lat === null) {
      // User is typing (no dropdown option picked yet — AddressAutocomplete
      // only sends lat:null while typing) — clear any metadata left over
      // from a PREVIOUS selection so stale city/county/coords can never be
      // saved against a completely different address string. State is kept
      // as-is (FL default or whatever was already picked) instead of being
      // cleared, so it never silently falls back to FL for a real out-of-
      // state address the user is still typing.
      newLocation.city = '';
      newLocation.zip_code = '';
      newLocation.latitude = null;
      newLocation.longitude = null;
      newLocation.county = '';
    } else {
      if (result.city) newLocation.city = result.city;
      if (result.state) newLocation.state = stateNameToCode(result.state) || newLocation.state;
      if (result.zip_code) newLocation.zip_code = result.zip_code;
      if (result.lat) newLocation.latitude = parseFloat(result.lat);
      if (result.lng) newLocation.longitude = parseFloat(result.lng);
      if (result.county) newLocation.county = result.county;
    }
    setForm({ ...form, location: newLocation });
  };

  const getProjectAddress = (p) => {
    const loc = p.location;
    if (!loc) return null;
    const parts = [loc.address, loc.city].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const handleZipChange = async (e) => {
    const zip = e.target.value;
    setForm(prev => ({...prev, location: {...prev.location, zip_code: zip}}));
    if (zip.length === 5 && !form.location.county) {
      try {
        const res = await api.get(`/geocode/county-by-zip?zip_code=${zip}`);
        if (res.data?.success && res.data.county) {
          setForm(prev => ({...prev, location: {...prev.location, county: res.data.county}}));
          toast.success(t('projects.countyDetected', { county: res.data.county }));
        }
      } catch { /* silent */ }
    }
  };

  const hasCoordinates = form.location.latitude && form.location.longitude;

  // Sorting helpers (replaces the removed status-filter tabs row).
  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 text-gray-300 inline-block ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#003366] inline-block ml-1" />
      : <ArrowDown className="w-3 h-3 text-[#003366] inline-block ml-1" />;
  };

  const sortedProjects = (() => {
    let arr = [...projects];
    if (pipelineFilter !== 'all') {
      const stages = STATUS_FILTER_STAGES[pipelineFilter] || [];
      arr = arr.filter(p => stages.includes(pipelines[p.id]?.stage || 'no_estimate'));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'title': va = a.title || ''; vb = b.title || ''; break;
        case 'customer': va = getCustomerName(a.customer_id) || ''; vb = getCustomerName(b.customer_id) || ''; break;
        case 'type': va = a.type || ''; vb = b.type || ''; break;
        case 'status': va = a.status || ''; vb = b.status || ''; break;
        case 'project_number':
        default: va = a.project_number || ''; vb = b.project_number || ''; break;
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
    return arr;
  })();

  const pipelineCounts = { all: projects.length, pending: 0, open: 0, accepted: 0 };
  projects.forEach(p => {
    const stage = pipelines[p.id]?.stage || 'no_estimate';
    for (const [bucket, stages] of Object.entries(STATUS_FILTER_STAGES)) {
      if (stages.includes(stage)) pipelineCounts[bucket]++;
    }
  });

  const handleCreateNewClient = async () => {
    if (!newClientForm.first_name.trim()) { toast.error('El nombre es requerido'); return; }
    setSavingClient(true);
    try {
      const res = await api.post('/contacts', newClientForm);
      setContacts(prev => [...prev, res.data]);
      setForm(prev => ({ ...prev, customer_id: res.data.id }));
      setShowNewClientForm(false);
      setNewClientForm({ first_name: '', last_name: '', company_name: '', phone: '', email: '', is_customer: true, type: 'customer', account_type: 'residential' });
      toast.success('Cliente creado y seleccionado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error creando cliente'); }
    finally { setSavingClient(false); }
  };

  return (
    <div data-testid="projects-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <FolderKanban className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" />
            {t('projects.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('projects.totalProjects', { count: projects.length })}</p>
        </div>
        <Button data-testid="new-project-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> {t('projects.newProject')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-projects" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search') + '...'} className="pl-10 h-9 bg-white" />
        </div>
      </div>

      {/* Status quick filter — punto I (Alian): "cliqué en pendiente y veo
          todo lo abierto y aceptado" — 3 baldes simples sobre el pipeline. */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="project-pipeline-filter-tabs">
        {['all', 'pending', 'open', 'accepted'].map(key => (
          <button
            key={key}
            onClick={() => setPipelineFilter(key)}
            data-testid={`project-filter-${key}`}
            className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${pipelineFilter === key ? 'bg-[#003366] text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100 bg-white border border-gray-100'}`}
          >
            {t(`projects.pipelineFilters.${key}`, key.charAt(0).toUpperCase() + key.slice(1))} {pipelineCounts[key] > 0 ? `(${pipelineCounts[key]})` : ''}
          </button>
        ))}
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : sortedProjects.length === 0 ? (
            <div className="py-16 text-center">
              <FolderKanban className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('projects.noProjects')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('projects.createFirstProject')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th onClick={() => toggleSort('project_number')} className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-project_number">#<SortIcon col="project_number" /></th>
                    <th onClick={() => toggleSort('title')} className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-title">{t('projects.projectTitle')}<SortIcon col="title" /></th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('projects.jobSite')}</th>
                    <th onClick={() => toggleSort('customer')} className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell cursor-pointer select-none hover:text-[#003366]" data-testid="sort-customer">{t('common.customer')}<SortIcon col="customer" /></th>
                    <th onClick={() => toggleSort('type')} className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-type">{t('common.type')}<SortIcon col="type" /></th>
                    <th onClick={() => toggleSort('status')} className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 cursor-pointer select-none hover:text-[#003366]" data-testid="sort-status">{t('common.status')}<SortIcon col="status" /></th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProjects.map((p) => {
                    const addr = getProjectAddress(p);
                    const ribbonLabel = computePaymentRibbon(invoicesByProject[p.id]);
                    return (
                      <tr key={p.id} data-testid={`project-row-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer">
                        <td className="px-5 py-3.5 text-sm font-mono text-gray-500">{p.project_number}</td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm font-medium text-gray-900">{p.title}</p>
                          {p.description && <p className="text-xs text-gray-400 truncate max-w-[200px]">{p.description}</p>}
                        </td>
                        <td className="px-5 py-3.5">
                          {addr ? (
                            <div className="flex items-start gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-gray-700 truncate max-w-[200px]">{addr}</p>
                                {p.location?.county && <p className="text-xs text-gray-400">{p.location.county} County</p>}
                              </div>
                            </div>
                          ) : p.location?.parcel_id ? (
                            // Fix E (2026-08-31): vacant lots with no street address yet,
                            // only a parcel number — show that instead of "Sin dirección".
                            <div className="flex items-start gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-indigo-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-gray-700 font-mono truncate max-w-[200px]">Parcel {p.location.parcel_id}</p>
                                {p.location?.county && <p className="text-xs text-gray-400">{p.location.county} County</p>}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300 italic">Sin dirección</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-600 hidden lg:table-cell">
                          <ContactNameStack contact={getCustomer(p.customer_id)} testId={`project-customer-${p.id}`} />
                        </td>
                        <td className="px-5 py-3.5"><TypeBadge type={p.type} /></td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-col gap-1 items-start">
                            {pipelines[p.id] ? (
                              <ProjectPipelineBadge stage={pipelines[p.id].stage} history={pipelines[p.id].history} compact size="xs" />
                            ) : (
                              <StatusBadge status={p.status} />
                            )}
                            {ribbonLabel && (
                              <span
                                data-testid={`project-payment-chip-${p.id}`}
                                className={`inline-flex items-center text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md text-white ${
                                  ribbonLabel === 'PAID' ? 'bg-emerald-600' :
                                  ribbonLabel === 'PARTIAL PAYMENT' ? 'bg-amber-500' :
                                  'bg-indigo-500'
                                }`}
                              >
                                {ribbonLabel}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`project-actions-${p.id}`} className="h-8 w-8">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                              <DropdownMenuItem data-testid={`project-forms-${p.id}`} onClick={() => setFormsDialog({ open: true, project: p })}><FileStack className="w-3.5 h-3.5 mr-2 text-violet-500" />Forms</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(p.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}
          </div>
        ) : sortedProjects.length === 0 ? (
          <Card className="bg-white border-gray-100">
            <CardContent className="py-12 text-center">
              <FolderKanban className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500 text-sm font-medium">{t('projects.noProjects')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('projects.createFirstProject')}</p>
            </CardContent>
          </Card>
        ) : (
          sortedProjects.map((p, idx) => {
            const addr = getProjectAddress(p);
            const ribbonLabel = computePaymentRibbon(invoicesByProject[p.id]);
            return (
              <Card key={p.id} data-testid={`project-mobile-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)} className="bg-white border-gray-100 shadow-sm mobile-card-enter cursor-pointer hover:shadow-md transition-shadow relative overflow-hidden" style={{ animationDelay: `${idx * 50}ms` }}>
                <PaymentRibbon label={ribbonLabel} />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.title}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{p.project_number}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1" onClick={e => e.stopPropagation()}><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`project-forms-mobile-${p.id}`} onClick={() => setFormsDialog({ open: true, project: p })}><FileStack className="w-3.5 h-3.5 mr-2 text-violet-500" />Forms</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(p.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {addr ? (
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <p className="text-xs text-gray-600 truncate">{addr}{p.location?.county ? ` - ${p.location.county} County` : ''}</p>
                    </div>
                  ) : p.location?.parcel_id && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                      <p className="text-xs text-gray-600 font-mono truncate">Parcel {p.location.parcel_id}{p.location?.county ? ` - ${p.location.county} County` : ''}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {pipelines[p.id] ? (
                      <ProjectPipelineBadge stage={pipelines[p.id].stage} history={pipelines[p.id].history} compact size="xs" />
                    ) : (
                      <StatusBadge status={p.status} />
                    )}
                    <TypeBadge type={p.type} />
                  </div>
                  {p.customer_id && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-400">{t('common.customer')}: </span>
                      <ContactNameStack contact={getCustomer(p.customer_id)} className="inline-block" primaryClassName="text-xs font-medium text-gray-700 inline" testId={`project-customer-mobile-${p.id}`} />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('projects.editProject') : t('projects.newProject')}</DialogTitle>
              {autoProjectNumber && (
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded">
                  {loadingProjectNumber ? <Loader2 className="w-3 h-3 animate-spin inline" /> : autoProjectNumber}
                </span>
              )}
            </div>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            {/* Title & Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('projects.projectTitle')} *</Label>
                <Input data-testid="project-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder={t('projects.projectTitlePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('common.type')}</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger data-testid="project-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map(tp => <SelectItem key={tp} value={tp}>{t(`projects.types.${tp}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('projects.officeNumber', 'Office Number')} <span className="text-gray-400 text-xs font-normal">({t('common.optional', 'optional')})</span></Label>
                <Input data-testid="project-office-number-input" value={form.office_number} onChange={e => setForm({...form, office_number: e.target.value})} placeholder="e.g. OFF-1042" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label>{t('common.customer')}</Label>
                {!showNewClientForm ? (
                  <div className="flex gap-2">
                    <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                      <SelectTrigger data-testid="project-customer-select" className="flex-1"><SelectValue placeholder={t('projects.selectCustomer')} /></SelectTrigger>
                      <SelectContent>
                        {contacts.filter(c => c.is_customer).map(c => (
                          <SelectItem key={c.id} value={c.id}>{contactPrimaryLabel(c)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowNewClientForm(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex-shrink-0" data-testid="new-client-btn">
                      <UserPlus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/50 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><UserPlus className="w-3 h-3" /> Nuevo Cliente</span>
                      <button type="button" onClick={() => setShowNewClientForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex gap-1 mb-1">
                      <button type="button" onClick={() => setNewClientForm({...newClientForm, account_type: 'residential'})} data-testid="new-client-type-residential" className={`flex-1 h-7 rounded text-[11px] font-medium border transition-colors ${newClientForm.account_type === 'residential' ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-500 border-gray-200'}`}>Residencial</button>
                      <button type="button" onClick={() => setNewClientForm({...newClientForm, account_type: 'commercial'})} data-testid="new-client-type-commercial" className={`flex-1 h-7 rounded text-[11px] font-medium border transition-colors flex items-center justify-center gap-1 ${newClientForm.account_type === 'commercial' ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-500 border-gray-200'}`}><Building2 className="w-3 h-3" /> Comercial</button>
                    </div>
                    {newClientForm.account_type === 'commercial' && (
                      <Input placeholder="Nombre de la empresa" value={newClientForm.company_name} onChange={e => setNewClientForm({...newClientForm, company_name: e.target.value})} className="h-8 text-xs" data-testid="new-client-company-name" />
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Nombre *" value={newClientForm.first_name} onChange={e => setNewClientForm({...newClientForm, first_name: e.target.value})} className="h-8 text-xs" data-testid="new-client-first-name" />
                      <Input placeholder="Apellido" value={newClientForm.last_name} onChange={e => setNewClientForm({...newClientForm, last_name: e.target.value})} className="h-8 text-xs" data-testid="new-client-last-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <Phone className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                        <Input placeholder="Teléfono" value={newClientForm.phone} onChange={e => setNewClientForm({...newClientForm, phone: e.target.value})} className="h-8 text-xs pl-6" />
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                        <Input placeholder="Email" type="email" value={newClientForm.email} onChange={e => setNewClientForm({...newClientForm, email: e.target.value})} className="h-8 text-xs pl-6" />
                      </div>
                    </div>
                    <Button type="button" onClick={handleCreateNewClient} disabled={savingClient} className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="save-new-client-btn">
                      {savingClient ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />} Crear y Seleccionar
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Job Site / Location with Autocomplete */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#3399FF]" />
                {t('projects.jobSite')}
              </Label>
              <AddressAutocomplete
                value={form.location.address}
                onChange={handleAddressChange}
                placeholder={t('projects.searchAddress')}
              />

              {/* County auto-detection indicator */}
              {form.location.county && form.location.latitude && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="county-detected">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm text-emerald-700 font-medium">{t('projects.countyDetected', { county: form.location.county })}</span>
                  <span className="text-xs text-emerald-500 ml-auto">{parseFloat(form.location.latitude).toFixed(4)}, {parseFloat(form.location.longitude).toFixed(4)}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Input value={form.location.city} onChange={e => setForm({...form, location: {...form.location, city: e.target.value}})} placeholder={t('common.city')} />
                <Select
                  value={form.location.state || 'FL'}
                  onValueChange={v => setForm({...form, location: {...form.location, state: v, county: v === 'FL' ? form.location.county : ''}})}
                >
                  <SelectTrigger data-testid="project-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.location.state === 'FL' ? (
                  <Select value={form.location.county} onValueChange={v => setForm({...form, location: {...form.location, county: v}})}>
                    <SelectTrigger data-testid="project-county-select"><SelectValue placeholder={t('common.county')} /></SelectTrigger>
                    <SelectContent>
                      {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.location.county}
                    onChange={e => setForm({...form, location: {...form.location, county: e.target.value}})}
                    placeholder={t('common.county')}
                    data-testid="project-county-input"
                  />
                )}
                <Input value={form.location.zip_code} onChange={handleZipChange} placeholder={t('common.zip')} data-testid="project-zip-input" />
              </div>

              {/* Verify on Map button */}
              {hasCoordinates && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMap(!showMap)}
                  className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="verify-map-btn"
                >
                  <Map className="w-4 h-4 mr-2" />
                  {showMap ? t('projects.hideMap') : t('projects.verifyOnMap')}
                </Button>
              )}

              {/* Map Verification */}
              {showMap && hasCoordinates && (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                  <WellMapVerification
                    address={form.location.address}
                    coordinates={{ lat: parseFloat(form.location.latitude), lng: parseFloat(form.location.longitude) }}
                    onVerified={(data) => {
                      toast.success('Ubicación verificada');
                      setShowMap(false);
                    }}
                    onClose={() => setShowMap(false)}
                  />
                </div>
              )}
            </div>

            {/* Parcel & Coordinates */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Navigation className="w-4 h-4 text-[#3399FF]" />
                {t('projects.landData')}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input data-testid="project-parcel" value={form.location.parcel_id} onChange={e => setForm({...form, location: {...form.location, parcel_id: e.target.value}})} placeholder={t('projects.parcelNumber')} />
                <Input data-testid="project-lat" value={form.location.latitude || ''} onChange={e => setForm({...form, location: {...form.location, latitude: e.target.value ? parseFloat(e.target.value) : null}})} placeholder={t('projects.latitude')} type="number" step="any" />
                <Input data-testid="project-lng" value={form.location.longitude || ''} onChange={e => setForm({...form, location: {...form.location, longitude: e.target.value ? parseFloat(e.target.value) : null}})} placeholder={t('projects.longitude')} type="number" step="any" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('common.description')}</Label>
              <Textarea data-testid="project-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder={t('common.description') + '...'} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>{t('common.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder={t('common.notes') + '...'} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-project-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('projects.saveChanges') : t('projects.createProject')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Forms Dialog */}
      {formsDialog.open && formsDialog.project && (
        <AttachCustomForm
          open={formsDialog.open}
          onClose={() => setFormsDialog({ open: false, project: null })}
          linkedType="project"
          linkedId={formsDialog.project.id}
          context={{
            project_id: formsDialog.project.id,
            customer_id: formsDialog.project.customer_id || '',
          }}
          county={formsDialog.project.location?.county || formsDialog.project.county || null}
          title={`Forms — ${formsDialog.project.project_number || formsDialog.project.title}`}
        />
      )}
    </div>
  );
}
